import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {AUTH_TOKEN, AUTH_AUTO_BILLING, AUTH_USER} from 'constants/AuthConstant';
import FirebaseService from 'services/FirebaseService';
import AuthService from 'services/AuthService';

const token = localStorage.getItem(AUTH_TOKEN);
const auto_billing = localStorage.getItem(AUTH_AUTO_BILLING) ?
	JSON.parse(localStorage.getItem(AUTH_AUTO_BILLING)) : {enabled: false, min_threshold: 0};

let user = null;

const storedUser = localStorage.getItem(AUTH_USER);
if (storedUser) {
	try {
		user = JSON.parse(storedUser);
	} catch (e) {
		console.error('Error parsing user from localStorage:', e);
		user = null;
	}
}

// Fallback to decoding token if AUTH_USER doesn't exist (for backward compatibility)
if (!user && token) {
	try {
		user = JSON.parse(atob(token.split('.')[1]));
		// Store it for next time
		localStorage.setItem(AUTH_USER, JSON.stringify(user));
	} catch (e) {
		user = null;
	}
}

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: token || null,
	user: user,
	credits: 0,
	auto_billing
};

const storeAuthToken = token => {
	localStorage.setItem(AUTH_TOKEN, token);
	// Extract user from JWT and store in localStorage
	try {
		const user = JSON.parse(atob(token.split('.')[1]));
		localStorage.setItem(AUTH_USER, JSON.stringify(user));
	} catch (e) {
		console.error('Error parsing user from token:', e);
	}
};

export const signIn = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
	const { email, password } = data;
	const response = await AuthService.login({email, password});
	if(response.msg === 'ok') {
		storeAuthToken(response.token);
		return {
			token: response.token,
			credits: response.credits,
			auto_billing: response.auto_billing
		};
	} else {
		return rejectWithValue(response.descr);
	}
})

export const signUp = createAsyncThunk('auth/register', async (data, { rejectWithValue }) => {
	const { company, name, email, phone, password, turnstile_token, id_token } = data;
	//console.log(company, name, email, phone, password, turnstile_token, id_token);
	try {
		const response = await AuthService
			.register({company, name, email, phone, password, turnstile_token, id_token});
		if(response.msg === 'ok') {
			storeAuthToken(response.token);
			return {
				token: response.token,
				credits: response.credits,
				auto_billing: response.auto_billing
			};
		} else {
			return rejectWithValue(response.descr);
		}
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error while registering. Please try again.')
	}
});

export const signInWithToken = createAsyncThunk('auth/signInWithToken', async (data, { rejectWithValue }) => {
	const { token, credits, auto_billing } = data;
	try {
		if (!token) return rejectWithValue('No token provided');

		storeAuthToken(token);
		return {
			token,
			credits,
			auto_billing
		};
	} catch (err) {
		return rejectWithValue('Invalid token authentication');
	}
});

export const signOut = createAsyncThunk('auth/logout',async () => {
    const response = await FirebaseService.signOutRequest()
	localStorage.removeItem(AUTH_TOKEN);
	localStorage.removeItem(AUTH_USER);
	localStorage.removeItem(AUTH_AUTO_BILLING);
    return response.data
})

export const signInWithGoogle = createAsyncThunk(
	'auth/signInWithGoogle',
	async (tokenData, { rejectWithValue }) => {
		//console.log(tokenData);
		try {
			storeAuthToken(tokenData.token);
			return {
				token: tokenData.token,
				credits: tokenData.credits,
				auto_billing: tokenData.auto_billing
			};
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error');
		}
	}
);

export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
    try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		},

		//These are retention credit reducers
		setRetentionCredits: (state, action) => {
			state.credits = action.payload;
		},
		incrementRetentionCredits: (state, action) => {
			state.credits += action.payload;
		},
		decrementRetentionCredits: (state, action) => {
			state.credits = Math.max(0, state.credits - action.payload);
		},

		//Auto Billing reducer
		updateAutoBilling: (state, action) => {
			state.auto_billing = action.payload; //this will be an object like {enabled: true, min_threshold: n}
			localStorage.setItem(AUTH_AUTO_BILLING, JSON.stringify(action.payload)); //So it survives refreshes.
		},

		updateUser: (state, action) => {
			console.log('Payload', action.payload); // Payload: {timezone: 'America/Phoenix'} or {name: 'John', email: 'john@example.com'}

			// Update the user object with the provided fields
			if (state.user && action.payload) {
				// Loop through the payload and update only the provided fields
				Object.keys(action.payload).forEach(key => {
					if (action.payload[key] !== undefined && action.payload[key] !== null && action.payload[key] !== '') {
						state.user[key] = action.payload[key];
					}
				});

				// Save updated user object to localStorage
				localStorage.setItem(AUTH_USER, JSON.stringify(state.user));
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				//console.log('AUTHENTICATED', action.payload);
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload.token;
				state.user = JSON.parse(atob(action.payload.token.split(/[..]/)[1])); //get user object from token
				state.credits = action.payload.credits;
				state.auto_billing = action.payload.auto_billing;

				//We need to save auto_billing settings in localStorage so it survives a reload.
				localStorage.setItem(AUTH_AUTO_BILLING, JSON.stringify(action.payload.auto_billing));
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload.token
				state.user = JSON.parse(atob(action.payload.token.split(/[..]/)[1])); //get user object from token
				state.credits = action.payload.credits;
				state.auto_billing = action.payload.auto_billing;
				localStorage.setItem(AUTH_AUTO_BILLING, JSON.stringify(action.payload.auto_billing));
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				//console.log('AUTHENTICATED', action.payload);
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload.token;
				state.user = state.user = JSON.parse(atob(action.payload.token.split('.')[1])); //get user object from token
				state.credits = action.payload.credits;
				state.auto_billing = action.payload.auto_billing;

				//We need to save auto_billing settings in localStorage so it survives a reload.
				localStorage.setItem(AUTH_AUTO_BILLING, JSON.stringify(action.payload.auto_billing));
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithToken.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithToken.fulfilled, (state, action) => {
				state.loading = false;
				state.redirect = '/';
				state.token = action.payload.token;

				// Parse user from the token we just received
				try {
					state.user = JSON.parse(atob(action.payload.token.split('.')[1]));
				} catch (e) {
					console.error("Error parsing user token", e);
				}

				state.credits = action.payload.credits;
				state.auto_billing = action.payload.auto_billing;

				// Save auto_billing settings in localStorage
				if (action.payload.auto_billing) {
					localStorage.setItem(AUTH_AUTO_BILLING, JSON.stringify(action.payload.auto_billing));
				}
			})
			.addCase(signInWithToken.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
	},
})

export const { 
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	setRetentionCredits,
	updateAutoBilling,
	updateUser
} = authSlice.actions

export default authSlice.reducer